import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { BitsApis } from "@app/core/APIs/bits-apis";
import {
    silentCrudApiOptions,
    silentRequestHeaders,
    unwrapResponse,
} from "@app/core/helpers/crud-response.helpers";
import { CrudApiService, GenResp } from "@ez-library/services/crud-api.service";
import { Observable, map } from "rxjs";
import {
    PhmsIntakeStagingApplyResultDto,
    PhmsIntakeStagingBatchDto,
    PhmsIntakeStagingRowCorrectionRequest,
    PhmsIntakeStagingUploadRequest,
} from "./phms-intake-staging.models";

@Injectable({ providedIn: "root" })
export class PhmsIntakeStagingService {
    private readonly bitsApis = inject(BitsApis);
    private readonly crudService = inject(CrudApiService);
    private readonly http = inject(HttpClient);

    uploadBatch(request: PhmsIntakeStagingUploadRequest): Observable<PhmsIntakeStagingBatchDto> {
        return this.crudService
            .postByUrl<PhmsIntakeStagingBatchDto>(
                this.bitsApis.core.manual.postPhmsIntakeStagingUpload(),
                buildUploadPayload(request),
                silentCrudApiOptions,
            )
            .pipe(map((response) => unwrapResponse(response)));
    }

    getBatch(batchId: number): Observable<PhmsIntakeStagingBatchDto> {
        return this.crudService
            .getbyUrl<PhmsIntakeStagingBatchDto>(
                this.bitsApis.core.manual.getPhmsIntakeStagingBatch(batchId),
                silentCrudApiOptions,
            )
            .pipe(map((response) => unwrapResponse(response)));
    }

    validateBatch(batchId: number): Observable<PhmsIntakeStagingBatchDto> {
        return this.crudService
            .postByUrl<PhmsIntakeStagingBatchDto>(
                this.bitsApis.core.manual.postPhmsIntakeStagingValidate(batchId),
                {},
                silentCrudApiOptions,
            )
            .pipe(map((response) => unwrapResponse(response)));
    }

    correctRow(
        batchId: number,
        rowId: number,
        correction: PhmsIntakeStagingRowCorrectionRequest,
    ): Observable<PhmsIntakeStagingBatchDto> {
        return this.http
            .patch<GenResp<PhmsIntakeStagingBatchDto> | PhmsIntakeStagingBatchDto>(
                this.bitsApis.core.manual.patchPhmsIntakeStagingRow(batchId, rowId),
                buildCorrectionPayload(correction),
                { headers: silentRequestHeaders, withCredentials: true },
            )
            .pipe(map((response) => unwrapResponse(response)));
    }

    applyBatch(batchId: number): Observable<PhmsIntakeStagingApplyResultDto> {
        return this.crudService
            .postByUrl<PhmsIntakeStagingApplyResultDto>(
                this.bitsApis.core.manual.postPhmsIntakeStagingApply(batchId),
                {},
                silentCrudApiOptions,
            )
            .pipe(map((response) => unwrapResponse(response)));
    }
}

function buildUploadPayload(request: PhmsIntakeStagingUploadRequest): PhmsIntakeStagingUploadRequest {
    return {
        programScopeKey: request.programScopeKey.trim(),
        sourceFileName: request.sourceFileName.trim(),
        rows: request.rows.map((row) => ({
            incarceratedPersonDisplayName: row.incarceratedPersonDisplayName.trim(),
            incarceratedPersonIdentifier: row.incarceratedPersonIdentifier.trim(),
            programScopeKey: row.programScopeKey.trim(),
        })),
    };
}

function buildCorrectionPayload(
    correction: PhmsIntakeStagingRowCorrectionRequest,
): PhmsIntakeStagingRowCorrectionRequest {
    const payload: PhmsIntakeStagingRowCorrectionRequest = {};
    if (correction.incarceratedPersonDisplayName !== undefined) {
        payload.incarceratedPersonDisplayName = correction.incarceratedPersonDisplayName.trim();
    }
    if (correction.incarceratedPersonIdentifier !== undefined) {
        payload.incarceratedPersonIdentifier = correction.incarceratedPersonIdentifier.trim();
    }
    if (correction.programScopeKey !== undefined) {
        payload.programScopeKey = correction.programScopeKey.trim();
    }
    return payload;
}
